import { vec3 } from 'gl-matrix';

/**
 * Spatial-query contract for physics consumers (player movement, projectile
 * collision, IK foot, NPC ground-snap, debug viz). Historically backed by an
 * adaptive voxel quadtree (the "volume grid", now retired); currently
 * implemented exclusively by the BVH-backed `TiledBvh` and its per-tile
 * `BvhSpatialGrid` (`engine/spatial/bvh/`). The interface name + the
 * `voxelSize` field are kept as-is during the phase-out — a rename pass
 * (Phase 4) will normalize to something like `SpatialQueryGrid` once the
 * dust settles. `voxelSize` is now a nominal step constant that consumers
 * use for sampling intervals; it has no concrete spatial meaning.
 */

export type VolumeHeightSample = {
  height: number;
  normal: vec3;
};

/**
 * Tagged floor height. `terrainOrigin` is true when the floor hit was on a
 * terrain leaf. Used by the tiled layer to substitute the smooth heightmap
 * Y for the returned `y` (so the player walks on the analytic surface rather
 * than the polygonal terrain mesh's quantization).
 */
export type FloorTagged = { y: number; terrainOrigin: boolean };

/**
 * First-hit result for `segmentHitWithNormal`. `t ∈ [0, 1]` is the parametric
 * distance along the segment; the hit point is `p0 + t * (p1 - p0)`. `normal`
 * points outward from the hit surface, suitable for direct reflection. On
 * the BVH path it's the exact triangle face normal.
 */
export type SegmentHit = { t: number; normal: vec3; isTerrain: boolean };

export interface SpatialQueryGrid {
  voxelSize: number;
  isSolidAt(x: number, y: number, z: number): boolean;
  getFloorHeight(x: number, y: number, z: number): number | null;
  getFloorHeightTagged(x: number, y: number, z: number): FloorTagged | null;
  getCeilingHeight(x: number, y: number, z: number): number | null;
  getCeilingHeightAbove(x: number, y: number, z: number): number | null;
  getFloorHeightBelow(x: number, y: number, z: number): number | null;
  getFloorHeightBelowTagged(x: number, y: number, z: number): FloorTagged | null;
  getFloorHeightBelowSmooth(x: number, y: number, z: number): number | null;
  getFloorHeightWithNormal(x: number, y: number, z: number): VolumeHeightSample | null;
  rayCrossesSolid(x0: number, y0: number, z0: number, x1: number, y1: number, z1: number): boolean;
  /**
   * Parametric distance along the segment `(p0 → p1)` to the first solid
   * surface intersection, or `null` if the segment is clear. Returns a value
   * in `[0, 1]` where 0 = at p0, 1 = at p1. Used by player movement to clamp
   * a candidate step to just-before-contact instead of rejecting it.
   */
  segmentHitDistance(
    x0: number, y0: number, z0: number,
    x1: number, y1: number, z1: number,
  ): number | null;
  /**
   * First-hit query along `(p0 → p1)` that also returns the surface normal
   * and terrain-origin flag at the hit. Used by projectile wall-bounce — the
   * caller reflects velocity off `normal` directly.
   */
  segmentHitWithNormal(
    x0: number, y0: number, z0: number,
    x1: number, y1: number, z1: number,
  ): SegmentHit | null;
  /**
   * True when a spatial-grid tile covers (x, z). Non-tiled grids cover the
   * whole world and always return true; the tiled grid only returns true for
   * currently-loaded tiles.
   */
  hasTileAt(x: number, z: number): boolean;
  /**
   * Iterate every leaf node and invoke `callback` with its world-space rect,
   * node type, byte offset (for callers that need to read leaf data inline),
   * and quadtree depth. For tiled grids, only currently-loaded tiles are visited.
   */
  forEachLeaf(callback: VolumeLeafCallback): void;
}

export type VolumeLeafCallback = (
  worldX: number,
  worldZ: number,
  worldW: number,
  worldD: number,
  nodeType: number,
  nodeOffset: number,
  depth: number,
) => void;

/**
 * Derive an axis-aligned wall normal at the hit point of a segment query
 * against a spatial index that only exposes `isSolidAt`. Probes one step in
 * the X and Z motion directions and treats the sides found solid as
 * contributing to the normal. The BVH path returns native triangle normals
 * from `raycastTlas` and doesn't go through this — only the cross-tile
 * fallback path approximates.
 */
export function approximateWallNormal(
  grid: SpatialQueryGrid,
  x0: number, y0: number, z0: number,
  x1: number, y1: number, z1: number,
  t: number,
  voxelSize: number,
): vec3 {
  const hx = x0 + (x1 - x0) * t;
  const hy = y0 + (y1 - y0) * t;
  const hz = z0 + (z1 - z0) * t;
  const dx = x1 - x0;
  const dz = z1 - z0;
  const sx = dx >= 0 ? 1 : -1;
  const sz = dz >= 0 ? 1 : -1;
  const solidInX = grid.isSolidAt(hx + voxelSize * sx, hy, hz);
  const solidInZ = grid.isSolidAt(hx, hy, hz + voxelSize * sz);
  const normal = vec3.create();
  if (solidInX) normal[0] = -sx;
  if (solidInZ) normal[2] = -sz;
  if (!solidInX && !solidInZ) {
    // Diagonal hit (probe found nothing in either cardinal axis) — use the
    // reversed motion direction.
    normal[0] = -sx;
    normal[2] = -sz;
  }
  vec3.normalize(normal, normal);
  return normal;
}
